/**
 * HealthKit bridge — communicates with the native HealthKitPlugin via Capacitor.
 *
 * Only functional on iOS native builds. All functions are no-ops or return
 * safe defaults on web / Android.
 */

import { registerPlugin } from "@capacitor/core";
import { isIOSNative } from "./platform";
import { log } from "./logger";

// -----------------------------------------------------------------------
// Plugin interface
// -----------------------------------------------------------------------

interface HealthKitPluginInterface {
  isAvailable(): Promise<{ available: boolean }>;
  requestReadAuthorization(): Promise<{
    authorized: boolean;
    error?: string | null;
    errorMessage?: string | null;
  }>;
  queryWorkouts(options: {
    fromDate: string;
    toDate: string;
  }): Promise<{ workouts: HealthKitWorkoutRaw[] }>;
}

export interface HealthKitWorkoutRaw {
  id: string;
  sport: string;
  startDate: string;
  endDate: string;
  durationSeconds: number;
  distanceMeters?: number;
  calories?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  sourceName?: string;
  sourceBundleId?: string;
}

// -----------------------------------------------------------------------
// Plugin registration (lazy — only resolves on native)
// -----------------------------------------------------------------------

const HealthKit = registerPlugin<HealthKitPluginInterface>("HealthKit");

// -----------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------

/** True only on iOS native builds (iPhone and iPad). */
export function isHealthKitPlatform(): boolean {
  return isIOSNative();
}

/** Check if HealthKit is available on this device. */
export async function isHealthKitAvailable(): Promise<boolean> {
  if (!isHealthKitPlatform()) return false;
  try {
    const { available } = await HealthKit.isAvailable();
    return available;
  } catch {
    return false;
  }
}

export interface HealthKitAuthResult {
  authorized: boolean;
  /** null = success, otherwise a machine-readable error code */
  error: string | null;
  errorMessage?: string;
}

/**
 * Request read-only HealthKit authorization.
 *
 * Important: For read-only permissions Apple returns authorized=true even when
 * the user denies access (privacy by design). authorized=false means a system
 * error (missing entitlement, HealthKit unavailable, etc.), NOT user denial.
 */
export async function requestHealthKitAuth(): Promise<HealthKitAuthResult> {
  log.debug("HEALTHKIT", "requestHealthKitAuth called");
  log.debug("HEALTHKIT", "isIOSNative():", isIOSNative());
  log.debug("HEALTHKIT", "isHealthKitPlatform():", isHealthKitPlatform());
  log.debug("HEALTHKIT", "userAgent:", navigator.userAgent);

  if (!isHealthKitPlatform()) {
    log.warn("HEALTHKIT", "Not an iOS native platform, skipping auth");
    return { authorized: false, error: "not_ios" };
  }
  try {
    // Pre-check availability
    log.debug("HEALTHKIT", "Calling isAvailable()...");
    const { available } = await HealthKit.isAvailable();
    log.debug("HEALTHKIT", "isAvailable result:", available);
    if (!available) {
      log.warn("HEALTHKIT", "HealthKit not available on this device");
      return { authorized: false, error: "healthkit_unavailable" };
    }

    log.debug("HEALTHKIT", "Calling requestReadAuthorization()...");
    const result = await HealthKit.requestReadAuthorization();
    log.debug("HEALTHKIT", "Authorization result:", JSON.stringify(result));
    return {
      authorized: result.authorized,
      error: result.error ?? null,
      errorMessage: result.errorMessage ?? undefined,
    };
  } catch (err) {
    log.error("HEALTHKIT", "Bridge error caught:", err);
    log.error("HEALTHKIT", "Error type:", typeof err);
    log.error("HEALTHKIT", "Error constructor:", (err as Record<string, unknown>)?.constructor);
    if (err instanceof Error) {
      log.error("HEALTHKIT", "Error message:", err.message);
      log.error("HEALTHKIT", "Error stack:", err.stack);
    } else if (err && typeof err === "object") {
      try { log.error("HEALTHKIT", "Error object:", JSON.stringify(err)); } catch { /* */ }
    }
    // Extract the most useful message for display
    let errorMsg: string;
    if (err instanceof Error) {
      errorMsg = err.message;
    } else if (err && typeof err === "object" && "message" in err) {
      errorMsg = String((err as Record<string, unknown>).message);
    } else {
      errorMsg = String(err);
    }
    return {
      authorized: false,
      error: "bridge_error",
      errorMessage: errorMsg,
    };
  }
}

/** Fetch workouts from HealthKit for a date range. */
export async function fetchHealthKitWorkouts(
  from: Date,
  to: Date
): Promise<HealthKitWorkoutRaw[]> {
  if (!isHealthKitPlatform()) return [];
  try {
    const { workouts } = await HealthKit.queryWorkouts({
      fromDate: from.toISOString(),
      toDate: to.toISOString(),
    });
    return workouts;
  } catch {
    return [];
  }
}
